import React, { useEffect, useState } from 'react';
import { reapplyStrategyPreview, reapplyStrategyApply } from '../../../services/StrategiesService';
import { processError } from '../../../services/ServiceUtils';

// Defaults do template AI Full (espelham FULL_SETUP_DEFAULT_CONFIG no backend). Usados pra
// pré-preencher o form quando a estratégia não tem config salvo (criada antes da Fase 1).
const AI_FULL_DEFAULTS = {
    walletPercent: 25,
    minConfidence: 0.65,
    stopLossPct: 3.0,
    downBounceArm: 'Não',
    atrSizing: 'Não',
    riskPerTradePct: 1.0,
    minProfitToSellPct: 0,
};

// Rótulos amigáveis por sourceKey p/ o preview do diff.
const ROLE_PT = {
    'AI_FULL:analysis': 'Análise por IA',
    'AI_FULL:buy': 'Compra',
    'AI_FULL:sell': 'Venda',
    'AI_FULL:hardstop': 'Stop de proteção',
    'AI_FULL:bear_exit': 'Saída em queda',
    'AI_FULL:rule_buy': 'Compra por regra',
    'AI_FULL:bounce': 'Repique em alta volatilidade',
};
const FIELD_PT = { conditions: 'condições', indexes: 'indicadores', schedule: 'agenda', name: 'nome' };
const roleLabel = (it) => ROLE_PT[it.sourceKey] || it.name;

// Rótulos amigáveis dos campos de ORDER TEMPLATE (Fase 4 — payoff, item 1: sizing/escada de TP).
const OT_FIELD_PT = {
    tp1Percent: 'TP1 (%)', tp1Quantity: 'TP1 (qtd. %)', tp2Percent: 'TP2 (%)', tp2Quantity: 'TP2 (qtd. %)',
    tp3Trailing: 'trailing', tp3TrailingStop: 'trailing stop (%)', tp3AtrMultiplier: 'multiplicador ATR do trailing',
    moveStopOnTp1: 'mover stop ao TP1', timeStopMinutes: 'stop por tempo (min)',
    quantityMultiplier: 'tamanho da posição', atrSizing: 'sizing por ATR', riskPerTradePct: 'risco por trade (%)',
};
// regimeSizing é um JSON {REGIME: pct} — renderiza só as chaves que mudaram ("HIGH_VOL 25%→0%")
// em vez do JSON cru, texto simples como pedido na spec.
function otChangeText(field, change) {
    if (field === 'regimeSizing') {
        let from = {}, to = {};
        try { from = JSON.parse(change.from || '{}'); } catch (e) { /* ignore */ }
        try { to = JSON.parse(change.to || '{}'); } catch (e) { /* ignore */ }
        const keys = [...new Set([...Object.keys(from), ...Object.keys(to)])];
        const parts = keys.filter(k => String(from[k]) !== String(to[k]))
            .map(k => `${k} ${from[k] ?? 0}%→${to[k] ?? 0}%`);
        return `dimensionamento por regime: ${parts.join(', ')}`;
    }
    return `${OT_FIELD_PT[field] || field} ${change.from ?? '-'}→${change.to ?? '-'}`;
}

/**
 * Modal "Reatualizar": re-materializa a estratégia do template+config atual e mostra o que muda
 * (preview do diff) antes de aplicar in-place (preserva histórico/IDs). Snapshot automático +
 * sim-gate no backend. props: { data: strategy, onSuccess }. v1: só AI Full single-símbolo.
 */
export default function ReapplyStrategyModal(props) {
    const strategy = props.data || {};
    const id = strategy.id;

    const [config, setConfig] = useState(AI_FULL_DEFAULTS);
    const [configSource, setConfigSource] = useState(null);
    const [preview, setPreview] = useState(null);
    const [loading, setLoading] = useState(false);
    const [applying, setApplying] = useState(false);
    const [error, setError] = useState('');
    const [done, setDone] = useState(null);
    const [autoSynced, setAutoSynced] = useState(false); // já auto-ajustou o form ao estado detectado?

    // Ao abrir (mudou o id), pré-preenche o form do config salvo (ou defaults) e limpa o estado.
    useEffect(() => {
        if (!id) return;
        setConfig({ ...AI_FULL_DEFAULTS, ...(strategy.config || {}) });
        setConfigSource(strategy.config ? 'saved' : 'defaults');
        setPreview(null);
        setError('');
        setDone(null);
        setAutoSynced(false);
    }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

    function getErr(err) {
        // processError devolve só o .error genérico — o sim-gate manda o MOTIVO em .reason
        // (ex.: qual situação da bateria quebrou). Sem ele o usuário fica sem ação possível.
        const data = err && err.response && err.response.data;
        if (data && data.error && data.reason) return `${data.error} Motivo: ${data.reason}`;
        const msg = processError(err);
        if (typeof msg === 'string') return msg;
        return (msg && (msg.reason || msg.error)) || 'Falha ao reatualizar.';
    }

    function setField(field, value) {
        setConfig(c => ({ ...c, [field]: value }));
        setPreview(null); // muda config → invalida o preview anterior
    }

    function onPreview(overrideConfig) {
        // Só aceita overrideConfig se for mesmo um config (a recursão passa um). Um SyntheticEvent
        // vindo do onClick tem .nativeEvent — nesse caso usa o config do estado.
        const isConfig = overrideConfig && typeof overrideConfig === 'object' && !overrideConfig.nativeEvent;
        const cfg = isConfig ? overrideConfig : config;
        overrideConfig = isConfig ? overrideConfig : undefined;
        setLoading(true); setError(''); setDone(null);
        if (!overrideConfig) setPreview(null);
        reapplyStrategyPreview(id, cfg)
            .then(r => {
                if (r.configSource) setConfigSource(r.configSource);
                // Auto-ajuste: estratégia SEM config salvo que JÁ tem o braço de repique — o form
                // defaultou 'Não' e proporia REMOVÊ-LO. Detecta, liga o repique no form e re-previa (1x).
                if (!autoSynced && r.configSource === 'defaults' && r.detected && r.detected.hasBounce && cfg.downBounceArm !== 'Sim') {
                    setAutoSynced(true);
                    const corrected = { ...cfg, downBounceArm: 'Sim' };
                    setConfig(corrected);
                    onPreview(corrected);
                    return;
                }
                setPreview(r);
                setLoading(false);
            })
            .catch(err => { setError(getErr(err)); setLoading(false); });
    }

    function onApply() {
        setApplying(true); setError('');
        reapplyStrategyApply(id, config)
            .then(r => setDone(r)) // recarrega só ao FECHAR (senão o reload esconde o aviso do braço novo)
            .catch(err => setError(getErr(err)))
            .finally(() => setApplying(false));
    }

    const s = preview && preview.summary;
    const otDiffs = (preview && preview.diff && preview.diff.orderTemplates) || [];
    const nothingToDo = s && s.updated === 0 && s.created === 0 && s.removed === 0 && otDiffs.length === 0;

    return (
        <div className="modal fade" id="modalReapplyStrategy" tabIndex="-1" role="dialog" aria-hidden="true">
            <div className="modal-dialog modal-lg modal-dialog-centered" role="document">
                <div className="modal-content">
                    <div className="modal-header">
                        <h5 className="modal-title">Reatualizar estratégia</h5>
                        <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Fechar"></button>
                    </div>
                    <div className="modal-body">
                        <p className="text-muted small mb-3">
                            Aplica a versão mais recente do modelo <strong>AI Full</strong> na estratégia
                            {strategy.name ? <> <strong>{strategy.name}</strong></> : ''}, sem perder o histórico
                            nem parar de operar. Você confere o que muda antes de aplicar; um ponto de restauração
                            é criado automaticamente.
                        </p>

                        {configSource === 'defaults' && (
                            <div className="alert alert-info py-2 small">
                                Esta estratégia foi criada antes de guardarmos as configurações. Confirme os valores
                                abaixo — eles serão usados como base do que a estratégia usa hoje.
                                {autoSynced && <><br />Detectamos que ela já tem o <strong>braço de repique</strong> — deixamos ligado aqui pra não removê-lo.</>}
                            </div>
                        )}

                        <div className="row g-3 mb-1">
                            <div className="col-6 col-md-4">
                                <label className="form-label small mb-1">% da carteira por compra</label>
                                <input type="number" className="form-control form-control-sm" min="1" max="100" step="1"
                                    value={config.walletPercent}
                                    onChange={e => setField('walletPercent', parseFloat(e.target.value))} />
                            </div>
                            <div className="col-6 col-md-4">
                                <label className="form-label small mb-1">Confiança mínima da IA</label>
                                <input type="number" className="form-control form-control-sm" min="0" max="1" step="0.05"
                                    value={config.minConfidence}
                                    onChange={e => setField('minConfidence', parseFloat(e.target.value))} />
                            </div>
                            <div className="col-6 col-md-4">
                                <label className="form-label small mb-1">Stop de proteção (%)</label>
                                <input type="number" className="form-control form-control-sm" min="0.5" max="50" step="0.5"
                                    value={config.stopLossPct}
                                    onChange={e => setField('stopLossPct', parseFloat(e.target.value))} />
                            </div>
                            <div className="col-6 col-md-4">
                                <label className="form-label small mb-1">Braço de repique (alta vol.)</label>
                                <select className="form-select form-select-sm"
                                    value={config.downBounceArm}
                                    onChange={e => setField('downBounceArm', e.target.value)}>
                                    <option value="Não">Não</option>
                                    <option value="Sim">Sim</option>
                                </select>
                            </div>
                            <div className="col-6 col-md-4">
                                <label className="form-label small mb-1">Tamanho por volatilidade (ATR)</label>
                                <select className="form-select form-select-sm"
                                    value={config.atrSizing}
                                    onChange={e => setField('atrSizing', e.target.value)}>
                                    <option value="Não">Não</option>
                                    <option value="Sim">Sim</option>
                                </select>
                            </div>
                            {config.atrSizing === 'Sim' && (
                                <div className="col-6 col-md-4">
                                    <label className="form-label small mb-1">Risco por trade (%)</label>
                                    <input type="number" className="form-control form-control-sm" min="0.1" max="10" step="0.1"
                                        value={config.riskPerTradePct}
                                        onChange={e => setField('riskPerTradePct', parseFloat(e.target.value))} />
                                </div>
                            )}
                            <div className="col-6 col-md-4">
                                <label className="form-label small mb-1">Lucro mínimo p/ vender por IA (%)</label>
                                <input type="number" className="form-control form-control-sm" min="0" max="20" step="0.5"
                                    value={config.minProfitToSellPct}
                                    onChange={e => setField('minProfitToSellPct', parseFloat(e.target.value))} />
                                <div className="form-text small">Pares estáveis (ex. USDC/USDT) usam teto de 0,1% — o valor acima é limitado a isso nesses pares.</div>
                            </div>
                        </div>

                        <div className="d-flex align-items-center gap-2 my-3">
                            <button type="button" className="btn btn-outline-primary btn-sm" onClick={() => onPreview()} disabled={loading || applying}>
                                {loading ? <span className="spinner-border spinner-border-sm" role="status" /> : 'Ver o que muda'}
                            </button>
                            <span className="text-muted small">O grid não é afetado (a grade se ajusta ao preço sozinha).</span>
                        </div>

                        {error && <div className="alert alert-danger">{error}</div>}

                        {done && (
                            <div className="alert alert-success">
                                Estratégia reatualizada. {done.armNote ? <><br />{done.armNote}</> : null}
                            </div>
                        )}

                        {preview && !done && (
                            <div className="border rounded p-2">
                                {nothingToDo ? (
                                    <div className="text-muted small mb-0">Nada muda — a estratégia já está na versão mais recente com este config.</div>
                                ) : (
                                    <>
                                        <div className="small mb-2">
                                            <strong>Resumo:</strong>{' '}
                                            {s.updated > 0 && <span className="badge bg-warning text-dark me-1">{s.updated} ajustada(s)</span>}
                                            {s.created > 0 && <span className="badge bg-success me-1">{s.created} nova(s)</span>}
                                            {s.removed > 0 && <span className="badge bg-secondary me-1">{s.removed} desativada(s)</span>}
                                            {s.unchanged > 0 && <span className="badge bg-light text-dark border me-1">{s.unchanged} sem mudança</span>}
                                        </div>
                                        {preview.diff.updated.length > 0 && (
                                            <div className="mb-2">
                                                {preview.diff.updated.map((it, i) => (
                                                    <div key={i} className="small">
                                                        ✏️ <strong>{roleLabel(it)}</strong>: muda {Object.keys(it.changes).map(f => FIELD_PT[f] || f).join(', ')}
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                        {preview.diff.created.length > 0 && (
                                            <div className="mb-2">
                                                {preview.diff.created.map((it, i) => (
                                                    <div key={i} className="small">➕ <strong>{roleLabel(it)}</strong>: será criada</div>
                                                ))}
                                            </div>
                                        )}
                                        {preview.diff.removed.length > 0 && (
                                            <div className="mb-2">
                                                {preview.diff.removed.map((it, i) => (
                                                    <div key={i} className="small">🚫 <strong>{roleLabel(it)}</strong>: será desativada (histórico preservado)</div>
                                                ))}
                                            </div>
                                        )}
                                        {otDiffs.length > 0 && (
                                            <div className="mb-2">
                                                {otDiffs.map((otd, i) => (
                                                    <div key={i} className="small">
                                                        📦 <strong>OT {ROLE_PT[otd.role] || otd.role}</strong>:{' '}
                                                        {Object.keys(otd.changes).map(f => otChangeText(f, otd.changes[f])).join('; ')}
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                        )}
                    </div>
                    <div className="modal-footer">
                        <button type="button" className="btn btn-secondary" data-bs-dismiss="modal"
                            onClick={() => { if (done && props.onSuccess) props.onSuccess(done); }}>
                            {done ? 'Fechar' : 'Cancelar'}
                        </button>
                        {!done && (
                            <button type="button" className="btn btn-primary" onClick={onApply}
                                disabled={applying || loading || !preview || nothingToDo}>
                                {applying ? <span className="spinner-border spinner-border-sm" role="status" /> : 'Reatualizar'}
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
